import _ from 'lodash';
import React from 'react';
import PropTypes from 'prop-types';

import { AttributePresentation } from 'models/configuration/attribute_def';
import connect from 'components/lib/connect';
import connectLocal from 'components/lib/connect_local';
import ContactAddressIcon from 'components/lib/icons/contact_address_icon';
import { ContactAttributeDef } from 'models/customer_profile_def';
import ContactEmailIcon from 'components/lib/icons/contact_email_icon';
import ContactIdIcon from 'components/lib/icons/contact_id_icon';
import ContactPhoneIcon from 'components/lib/icons/contact_phone_icon';
import CustomerAddressField from 'components/customer/profile/customer_address_field';
import CustomerContactField from 'components/customer/profile/customer_contact_field';
import CustomerContactMenu, { Channel } from 'components/customer/profile/customer_contact_menu';
import CustomerEmailFields from 'components/customer/profile/customer_email_fields';
import CustomerPhoneFields from 'components/customer/profile/customer_phone_fields';
import CustomerProfileModel from 'models/customer_profile';
import { EndpointTypes } from 'models/endpoint';
import FbUserProfile from 'components/customer/profile/__specs__/fb_user_profile';
import ProfileCard from 'components/customer/profile/profile_card';
import ExternalCustomerAddressField from 'components/customer/profile/external_customer_address_field';

export function CustomerProfile({
  contactAttributes,
  hasErrors,
  isAddingEmail,
  isAddingPhone,
  isLoading,
  isResponsePending,
  isInstagramEnabled,
  isWhatsAppEnabled,
  isTwitterEnabled,
  onAddChannel,
  onDoneAddingEmail,
  onDoneAddingPhone,
  profile,
}) {
  if (!isLoading && !profile) {
    return null;
  }

  let menu = <CustomerContactMenu isDisabled={isResponsePending || hasErrors} onSelectItem={onAddChannel} />;

  return (
    <ProfileCard isLoading={isLoading} title="Contact" menu={menu} data-aid="customerContactSection">
      {renderAddress()}
      {renderEmails()}
      {renderPhones()}
      {renderFacebook()}
      {renderExternalCustomerAddresses()}
      {renderExternalCustomerId()}
    </ProfileCard>
  );

  function renderAddress() {
    if (hasPresentation('address', AttributePresentation.HIDDEN)) {
      return null;
    }

    return (
      <div className="customerProfile-addressRow" key="address">
        <ContactAddressIcon className="customerProfile-addressIcon" />
        <CustomerAddressField isReadOnly={hasPresentation('address', AttributePresentation.VISIBLE)} />
      </div>
    );
  }

  function renderEmails() {
    return (
      <div className="customerProfile-emailRow" key="email">
        <ContactEmailIcon className="customerProfile-emailIcon" />
        <CustomerEmailFields isAdding={isAddingEmail} onDone={onDoneAddingEmail} />
      </div>
    );
  }

  function renderPhones() {
    return (
      <div className="customerProfile-phoneRow" key="phone">
        <ContactPhoneIcon className="customerProfile-phoneIcon" />
        <CustomerPhoneFields isAdding={isAddingPhone} onDone={onDoneAddingPhone} />
      </div>
    );
  }

  function renderFacebook() {
    if (
      !profile ||
      (_.isEmpty(profile.fbMessengerUserIds) && !profile.fbUserProfile) ||
      hasPresentation('fbUserProfile', AttributePresentation.HIDDEN)
    ) {
      return null;
    }

    return (
      <FbUserProfile
        className="customerProfile-facebookRow customerProfile-contactDetails-row customerProfile-contactDetails-readonly"
        profile={profile.fbUserProfile}
        key="facebook"
      />
    );
  }

  function renderExternalCustomerAddresses() {
    if (hasPresentation('externalCustomerAddresses', AttributePresentation.HIDDEN)) {
      return null;
    }

    if (_.isEmpty(profile.getExternalCustomerAddresses())) {
      return null;
    }
    const externalCustomerAddresses = profile.getExternalCustomerAddresses();
    return _.map(externalCustomerAddresses, address => (
      <ExternalCustomerAddressField
        key={address}
        address={address}
        isInstagramEnabled={isInstagramEnabled}
        isWhatsAppEnabled={isWhatsAppEnabled}
        isTwitterEnabled={isTwitterEnabled}
      />
    ));
  }

  function renderExternalCustomerId() {
    if (hasPresentation('externalCustomerId', AttributePresentation.HIDDEN)) {
      return null;
    }

    return (
      <div className="customerProfile-contactDetails-row customerProfile-idRow" key="externalCustomerId">
        <ContactIdIcon className="customerProfile-idIcon" />
        <dd className="customerProfile-contactField customerProfile-idField">
          <CustomerContactField
            attr="externalCustomerId"
            isReadOnly={hasPresentation('externalCustomerId', AttributePresentation.VISIBLE)}
            label="Customer ID"
          />
        </dd>
      </div>
    );
  }

  function hasPresentation(attr, presentation) {
    const contactAttribute = _.find(contactAttributes, contactAttr => contactAttr.attr === attr);
    return contactAttribute && contactAttribute.presentation === presentation;
  }
}

CustomerProfile.propTypes = {
  contactAttributes: PropTypes.arrayOf(PropTypes.instanceOf(ContactAttributeDef)),
  hasErrors: PropTypes.bool,
  isAddingEmail: PropTypes.bool,
  isAddingPhone: PropTypes.bool,
  isLoading: PropTypes.bool,
  isResponsePending: PropTypes.bool,
  isInstagramEnabled: PropTypes.bool,
  isWhatsAppEnabled: PropTypes.bool,
  isTwitterEnabled: PropTypes.bool,
  onAddChannel: PropTypes.func.isRequired,
  onDoneAddingEmail: PropTypes.func.isRequired,
  onDoneAddingPhone: PropTypes.func.isRequired,
  profile: PropTypes.instanceOf(CustomerProfileModel),
};

const AddHandlingCustomerProfile = connectLocal({ mapSetStateToProps })(CustomerProfile);
export { AddHandlingCustomerProfile };

function mapSetStateToProps(setState) {
  return {
    onAddChannel: channelName => {
      if (channelName === Channel.EMAIL) {
        setState({ isAddingEmail: true });
      } else if (channelName === Channel.PHONE) {
        setState({ isAddingPhone: true });
      }
    },
    onDoneAddingEmail: () => setState({ isAddingEmail: false }),
    onDoneAddingPhone: () => setState({ isAddingPhone: false }),
  };
}

AddHandlingCustomerProfile.propTypes = {
  contactAttributes: PropTypes.arrayOf(PropTypes.instanceOf(ContactAttributeDef)),
  hasErrors: PropTypes.bool,
  isLoading: PropTypes.bool,
  isResponsePending: PropTypes.bool,
  isInstagramEnabled: PropTypes.bool,
  isWhatsAppEnabled: PropTypes.bool,
  isTwitterEnabled: PropTypes.bool,
  profile: PropTypes.instanceOf(CustomerProfileModel),
};

export default connect(mapStateToProps)(AddHandlingCustomerProfile);

function mapStateToProps({ getProvider }) {
  let profileProvider = getProvider('profile');
  let defProvider = getProvider('customerProfileDef');
  let channelConfiguration = getProvider('channelConfiguration').get();
  let isInstagramEnabled =
    channelConfiguration && channelConfiguration.isChannelEnabled(EndpointTypes.INSTAGRAM_DIRECT);
  let isWhatsAppEnabled = channelConfiguration && channelConfiguration.isChannelEnabled(EndpointTypes.WHATSAPP);
  let isTwitterEnabled = channelConfiguration && channelConfiguration.isChannelEnabled(EndpointTypes.TWITTER);

  return {
    contactAttributes: _.get(defProvider.get(), 'contactAttributes'),
    hasErrors: !_(profileProvider.getErrors()).isEmpty(),
    isLoading: defProvider.isLoading() || profileProvider.isLoading(),
    isResponsePending: profileProvider.isPending(),
    isInstagramEnabled,
    isWhatsAppEnabled,
    isTwitterEnabled,
    profile: profileProvider.get(),
  };
}



// WEBPACK FOOTER //
// ./src/scripts/presentation/components/customer/customer_profile.jsx